import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AdminStripeCoupon } from "schemas/admin";
import { SetAlertType } from "schemas/functions";
import { Plans } from "schemas/payments";

import { fetcherAuth } from "utils/api";

export type StripeCouponDrawerMode =
  | { kind: "edit"; coupon: AdminStripeCoupon }
  | { kind: "create" };

type Props = {
  mode: StripeCouponDrawerMode | null;
  onClose: () => void;
  onSaved: (saved: AdminStripeCoupon) => void;
  setAlert: SetAlertType;
};

type FormState = {
  name: string;
  description: string;
  stripeCouponId: string;
  stripePromoCodeId: string;
  checkoutCode: string;
  discountPercentage: string;
  applicablePlans: string[];
  showablePlans: string[];
  startDate: string;
  endDate: string;
  isActive: boolean;
};

const ALL_PLANS: string[] = Object.values(Plans);

// New coupons default to every plan. An empty list also means "all plans" on
// the backend, so admins can narrow this down (or clear it) as needed.
const emptyForm: FormState = {
  name: "",
  description: "",
  stripeCouponId: "",
  stripePromoCodeId: "",
  checkoutCode: "",
  discountPercentage: "",
  applicablePlans: ALL_PLANS,
  showablePlans: [],
  startDate: "",
  endDate: "",
  isActive: true,
};

const timestampToInputDate = (ts?: number | null): string => {
  if (!ts) return "";
  return new Date(ts * 1000).toISOString().slice(0, 10);
};

const inputDateToIso = (value: string): string | null => {
  if (!value) return null;
  // Treat the date input as UTC midnight to match how the backend stores
  // start/end dates (window comparisons use today @ UTC).
  return new Date(`${value}T00:00:00Z`).toISOString();
};

const toForm = (coupon: AdminStripeCoupon): FormState => ({
  name: coupon.name || "",
  description: coupon.description || "",
  stripeCouponId: coupon.stripeCouponId || "",
  stripePromoCodeId: coupon.stripePromoCodeId || "",
  checkoutCode: coupon.checkoutCode || "",
  discountPercentage:
    coupon.discountPercentage != null ? String(coupon.discountPercentage) : "",
  applicablePlans: coupon.applicablePlans || [],
  showablePlans: coupon.showableToPlans || [],
  startDate: timestampToInputDate(coupon.startDate),
  endDate: timestampToInputDate(coupon.endDate),
  isActive: coupon.isActive,
});

const EditStripeCouponDrawer = ({
  mode,
  onClose,
  onSaved,
  setAlert,
}: Props) => {
  const { getToken } = useAuth();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);

  const modeKey =
    mode?.kind === "edit" ? `edit-${mode.coupon.id}` : mode?.kind ?? null;

  useEffect(() => {
    if (mode?.kind === "edit") {
      setForm(toForm(mode.coupon));
    } else {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeKey]);

  if (!mode) return null;

  const isCreate = mode.kind === "create";
  const subject = mode.kind === "edit" ? mode.coupon : null;

  const handleField =
    <K extends keyof FormState>(key: K) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value;
      setForm((prev) => ({ ...prev, [key]: value }) as FormState);
    };

  const buildBody = () => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    stripeCouponId: form.stripeCouponId.trim() || null,
    stripePromoCodeId: form.stripePromoCodeId.trim() || null,
    checkoutCode: form.checkoutCode.trim() || null,
    discountPercentage:
      form.discountPercentage === "" ? null : Number(form.discountPercentage),
    applicablePlans: form.applicablePlans,
    showableToPlans: form.showablePlans,
    startDate: inputDateToIso(form.startDate),
    endDate: inputDateToIso(form.endDate),
    isActive: form.isActive,
  });

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = isCreate
        ? `/api/admin/stripe-coupons`
        : `/api/admin/stripe-coupons/${subject!.id}`;
      const method = isCreate ? "POST" : "PUT";
      const res = await fetcherAuth(getToken, url, method, {}, buildBody());
      onSaved(res.stripeCoupon);
    } catch (error: any) {
      setAlert(error?.message || "Failed to save coupon", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 560 } } }}
    >
      <Box sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5">
            <strong>{isCreate ? "New Coupon" : "Edit Coupon"}</strong>
          </Typography>
          <IconButton onClick={onClose}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Stack>

        {subject && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            ID: {subject.id}
          </Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Name (internal slug)"
              fullWidth
              value={form.name}
              onChange={handleField("name")}
              helperText="e.g. memorial_sale. Internal identifier; not shown to users."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={handleField("description")}
              helperText="Banner copy shown to users."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Stripe Coupon ID"
              fullWidth
              value={form.stripeCouponId}
              onChange={handleField("stripeCouponId")}
              helperText="Paste from Stripe Dashboard (the coupon ID)."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Stripe Promo Code ID"
              fullWidth
              value={form.stripePromoCodeId}
              onChange={handleField("stripePromoCodeId")}
              helperText="Paste the promotion_code ID (starts with promo_)."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Checkout Code"
              fullWidth
              value={form.checkoutCode}
              onChange={handleField("checkoutCode")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Discount Percentage"
              type="number"
              fullWidth
              value={form.discountPercentage}
              onChange={handleField("discountPercentage")}
              inputProps={{ min: 0, max: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Start Date (UTC)"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.startDate}
              onChange={handleField("startDate")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="End Date (UTC)"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={form.endDate}
              onChange={handleField("endDate")}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={ALL_PLANS}
              value={form.applicablePlans}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, applicablePlans: value }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Applicable Plans"
                  helperText="Plans the coupon works on if a user checks out with it. Leave blank to apply to all plans."
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={ALL_PLANS}
              value={form.showablePlans}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, showablePlans: value }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Showable Plans"
                  helperText="Users currently on these plans will see the coupon banner. Leave blank to show it to users on all plans."
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={handleField("isActive")}
                  name="isActive"
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
          <LoadingButton
            loading={saving}
            variant="contained"
            onClick={handleSave}
          >
            Save
          </LoadingButton>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default EditStripeCouponDrawer;
